<script setup>
// Astra DS · TiendaIcono — el acceso a una app en la cabecera, solo el logotipo.
//
// Figma `TiendaIcono 2396:28556`, `Tienda` = TestFlight `2396:28542` · Google
// Play `2396:28550`. Entrega `docs/entregas/2026-10-06-donar.md`: sustituye a
// `StoreBadge` en la celda `tiendas` para hacerle sitio a Donar en la barra.
//
// Celda de 46 × 46 con filete `border/default` a la izquierda, sin superficie
// propia y sin radio, como cualquier celda de la consola. Dentro, el logotipo
// de marca a color a 18 de alto, que no cambia con el estado.
//
// El nombre de la tienda ya no se lee en la celda: lo dice un `Tooltip` que
// cuelga del filete de la cabecera al pasar el ratón o con el foco. Ese tooltip
// lo pinta `SiteHeader` fuera del `<header>` —que lleva `overflow: hidden` y lo
// recortaría—; esta celda solo avisa de cuándo enseñarlo y dónde está.
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { medir } from "@/services/medicion";
import TestFlightIcon from "@/components/icons/TestFlightIcon.vue";
import GooglePlayIcon from "@/components/icons/GooglePlayIcon.vue";

const props = defineProps({
  tienda: {
    type: String,
    required: true,
    validator: (v) => ["testflight", "google-play"].includes(v),
  },
  to: { type: String, required: true },
});

const emit = defineEmits(["mostrar", "ocultar"]);

const { t } = useI18n();

const TEXTOS = {
  testflight: { kicker: t("marco.cabecera.pruebalaEn"), nombre: "TestFlight" },
  "google-play": { kicker: t("marco.cabecera.disponibleEn"), nombre: "Google Play" },
};
const texto = computed(() => TEXTOS[props.tienda]);

const esExterno = computed(() => /^https?:\/\//.test(props.to));
const atributos = computed(() =>
  esExterno.value
    ? { href: props.to, target: "_blank", rel: "noopener" }
    : { to: props.to },
);

function mostrar(evento) {
  emit("mostrar", { ...texto.value, celda: evento.currentTarget });
}
</script>

<template>
  <component
    :is="esExterno ? 'a' : 'router-link'"
    v-bind="atributos"
    class="tienda-icono"
    :aria-label="`${texto.kicker} ${texto.nombre}`"
    @mouseenter="mostrar"
    @focus="mostrar"
    @mouseleave="emit('ocultar')"
    @blur="emit('ocultar')"
    @click="medir('descarga_app', { plataforma: tienda === 'testflight' ? 'ios' : 'android' })"
  >
    <TestFlightIcon v-if="tienda === 'testflight'" class="tienda-icono__logo" :size="18" />
    <GooglePlayIcon v-else class="tienda-icono__logo tienda-icono__logo--gp" />
  </component>
</template>

<style scoped lang="scss">
.tienda-icono {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 46px;
  align-self: stretch;
  border-left: 1px solid var(--color-border-default);
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
}

.tienda-icono__logo {
  flex: none;
}

// El de Google Play viene con `height: 1em`, así que se le da el cuerpo: 18.
.tienda-icono__logo--gp {
  font-size: 18px;
}
</style>
